/**
 * Widgets Page
 * ვიჯეტების გვერდი
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import {
  Card,
  Button,
  DataTable,
  Modal,
  Spinner,
  useNotification
} from '@aiia/portal-core';
import type { TableColumn } from '@aiia/portal-core';
import {
  useWidgets,
  useDeleteWidget,
  useSetDefaultWidget,
  useToggleWidget
} from '../hooks/useWidgets';
import type { WidgetConfig } from '../features/api/widgets';

export default function WidgetsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lp = useLocalizedPath();
  const notification = useNotification();
  const { widgets, isLoading, refetch } = useWidgets();
  const deleteWidget = useDeleteWidget();
  const setDefaultWidget = useSetDefaultWidget();
  const toggleWidget = useToggleWidget();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!widgetToDelete) return;
    try {
      await deleteWidget.mutateAsync(widgetToDelete);
      notification.success(t('widgets.widgetDeleted'));
      setDeleteModalOpen(false);
      setWidgetToDelete(null);
      refetch();
    } catch (error: unknown) {
      notification.error(t('widgets.widgetDeleteFailed'));
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultWidget.mutateAsync(id);
      notification.success(t('widgets.defaultUpdated'));
      refetch();
    } catch (error: unknown) {
      notification.error(t('widgets.defaultUpdateFailed'));
    }
  };

  const handleToggle = async (id: string, currentEnabled: boolean) => {
    try {
      await toggleWidget.mutateAsync({ id, enabled: currentEnabled });
      notification.success(currentEnabled ? t('widgets.widgetDisabled') : t('widgets.widgetEnabled'));
      refetch();
    } catch (error: unknown) {
      notification.error(t('widgets.toggleFailed'));
    }
  };

  const confirmDelete = (id: string) => {
    setWidgetToDelete(id);
    setDeleteModalOpen(true);
  };

  const columns: TableColumn<WidgetConfig & Record<string, unknown>>[] = [
    {
      key: 'name',
      header: t('widgets.name'),
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{String(value)}</span>
          {row.isDefault && (
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
              {t('common.default')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'assistantName',
      header: t('widgets.assistantName'),
    },
    {
      key: 'position',
      header: t('widgets.position'),
      render: (value) => (
        <span className="capitalize">{String(value).replace('-', ' ')}</span>
      ),
    },
    {
      key: 'theme',
      header: t('widgets.theme'),
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded border"
            style={{ backgroundColor: (row.theme as WidgetConfig['theme'])?.primaryColor || '#3B82F6' }}
          />
          <span className="text-sm text-gray-500">{(row.theme as WidgetConfig['theme'])?.primaryColor || '#3B82F6'}</span>
        </div>
      ),
    },
    {
      key: 'enabled',
      header: t('common.status'),
      render: (_, row) => (
        <span className={`px-2 py-0.5 text-xs rounded-full ${
          row.enabled
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-100 text-gray-500'
        }`}>
          {row.enabled ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      key: 'updatedAt',
      header: t('widgets.lastUpdated'),
      render: (value) => formatDate(String(value)),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (_, row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleToggle(row.id as string, row.enabled as boolean)}
          >
            {row.enabled ? t('common.disable') : t('common.enable')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => navigate(lp(`/widgets/${row.id}/edit`))}>
            {t('common.edit')}
          </Button>
          {!row.isDefault && (
            <Button size="sm" variant="secondary" onClick={() => handleSetDefault(row.id as string)}>
              {t('widgets.setDefault')}
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={() => confirmDelete(row.id as string)}>
            {t('common.delete')}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('widgets.title')}</h1>
          <p className="text-gray-600 mt-1">{t('widgets.subtitle')}</p>
        </div>
        <Button onClick={() => navigate(lp('/widgets/new'))}>
          {t('widgets.createWidget')}
        </Button>
      </div>

      <Card>
        <DataTable
          columns={columns}
          data={(widgets || []) as (WidgetConfig & Record<string, unknown>)[]}
          emptyMessage={t('widgets.emptyMessage')}
        />
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setWidgetToDelete(null);
        }}
        title={t('widgets.deleteTitle')}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {t('widgets.deleteConfirm')}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteModalOpen(false);
                setWidgetToDelete(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteWidget.isPending}>
              {deleteWidget.isPending ? t('common.deleting') : t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
